import { SLUG_TO_ROUTE } from '@/app-shell/settings/atoms/settingsModalSlugs';
import { appRegistry } from '@/composition/registry-atom';
import {
  settingsScreenHostFor,
  settingsScreenHosts,
  type SettingsScreenHostMap,
} from '@/composition/settings-screen-hosts';
import { type SettingsScreenContribution } from '@heidi/runtime/shell';
import { lazy, type ComponentType } from 'react';
import { settingsImportWithRetry } from './settingsImportWithRetry';

/**
 * Map from `SettingsItem.href` (the canonical key used everywhere in the
 * settings IA) to a dynamic import that resolves to the screen component for
 * that domain.
 *
 * Each importer is invoked at least twice during a normal flow:
 *
 * 1. As the loader passed to `React.lazy()` in `SCREENS` below — the path
 *    React takes when the user opens a section that hasn't been loaded yet.
 * 2. Imperatively via {@link preloadSettingsScreen} — the path we use to
 *    *anticipate* a section load and start fetching the chunk earlier than
 *    React would on its own (e.g. on sidebar hover, or synchronously in
 *    `useSettingsModal.open()` so the chunk fetch races with React's
 *    commit-and-mount work instead of waiting for it).
 *
 * Webpack/Next dedupe `import()` calls per module specifier, so calling an
 * importer twice is cheap — the second call awaits the same in-flight
 * Promise. This is what makes preloading safe regardless of order.
 *
 * Each importer is also wrapped in {@link settingsImportWithRetry} so a
 * transient chunk-fetch failure (flaky network, intermittent CDN edge
 * error) gets a couple of backed-off retries before bubbling out. The
 * specific case this defends against — a mid-session Settings open that
 * crashes the app and tears down `SessionRecordingProvider` (pausing the
 * live recording) — is laid out in `docs/design-docs/settings-modal-design-decisions.md`
 * §2.17. Stale-hash failures after a deploy will still fail every retry
 * and surface inline via `SettingsModalScreenErrorBoundary`.
 *
 * A route's screen is not always the app's own import: {@link
 * resolveScreenImporters} lets a workspace package serve the screen body for a
 * route it owns, and falls back to the map below for every route no package
 * claims. `/settings/coding` is served by `@heidi/coding/contributions`; the map
 * below still loads every other route, and still loads that one if the
 * contribution is ever withdrawn.
 *
 * A package-served screen may also need app-side wiring before it renders; that
 * wiring is composition's, keyed by contribution id in
 * `src/composition/settings-screen-hosts.ts`, and this module's only job is to
 * load it beside the screen.
 *
 * Keep this list in sync with the IA in `useSettingsList` and with the
 * `ROUTE_TO_SLUG` map in `src/app-shell/settings/atoms/settingsModalSlugs.ts`.
 * Drift against the slug map is caught by the parity test in
 * `settingsScreenLoaders.test.ts` (`'covers exactly the routes declared in
 * ROUTE_TO_SLUG (drift guard)'`).
 */
type ScreenImporter = () => Promise<{ default: ComponentType }>;

/**
 * Raw dynamic imports, one per settings route. Wrapped with
 * {@link settingsImportWithRetry} below before being exposed as
 * {@link screenImporters} — keeps the per-route entries small and
 * impossible to forget to wrap when adding a new section.
 *
 * Exported for {@link resolveScreenImporters}' parity suite, which asserts that
 * with no contributions every route resolves to *this* importer by reference.
 */
export const rawScreenImporters: Record<string, ScreenImporter> = {
  '/settings/account': () =>
    import('@/app-shell/settings/account/AccountSettings').then((mod) => ({
      default: mod.AccountSettings,
    })),
  '/settings/billing': () =>
    import('./settings-screen-entries/billing').then((mod) => ({
      default: mod.BillingSettings,
    })),
  '/settings/memory': () =>
    import('./settings-screen-entries/memory').then((mod) => ({
      default: mod.MemorySettings,
    })),
  '/settings/messaging-apps': () =>
    import('@/app-shell/settings/messaging-apps/MessagingAppsSettings').then((mod) => ({
      default: mod.MessagingAppsSettings,
    })),
  '/settings/display-controls': () =>
    import('@/app-shell/settings/display-controls/DisplayControlsSettings').then(
      (mod) => ({ default: mod.DisplayControlsSettings })
    ),
  '/settings/data-management': () =>
    import('@/app-shell/settings/data-management/DataManagementSettings').then((mod) => ({
      default: mod.DataManagementSettings,
    })),
  '/settings/defaults': () =>
    import('@/app-shell/settings/defaults/DefaultsSettings').then((mod) => ({
      default: mod.DefaultsSettings,
    })),
  '/settings/dictate': () =>
    import('./settings-screen-entries/dictate').then((mod) => ({
      default: mod.DictateSettings,
    })),
  '/settings/notifications': () =>
    import('./settings-screen-entries/notifications').then((mod) => ({
      default: mod.NotificationsSettings,
    })),
  '/settings/evidence': () =>
    import('./settings-screen-entries/evidence').then((mod) => ({
      default: mod.EvidenceSettings,
    })),
  '/settings/comms': () =>
    import('./settings-screen-entries/comms').then((mod) => ({
      default: mod.CommsSettings,
    })),
  '/settings/comms-data-management': () =>
    import('./settings-screen-entries/comms-data-management').then((mod) => ({
      default: mod.CommsDataManagementSettings,
    })),
  '/settings/summaries': () =>
    import('./settings-screen-entries/summaries').then((mod) => ({
      default: mod.SummariesSettings,
    })),
  '/settings/blocked-numbers': () =>
    import('./settings-screen-entries/blocked-numbers').then((mod) => ({
      default: mod.BlockedNumbersSettings,
    })),
  '/settings/comms-integrations': () =>
    import('./settings-screen-entries/comms-integrations').then((mod) => ({
      default: mod.CommsIntegrationsSettings,
    })),
  '/settings/prechart': () =>
    import('./settings-screen-entries/prechart').then((mod) => ({
      default: mod.PrechartSettings,
    })),
  '/settings/heidi-labs': () =>
    import('@/app-shell/settings/heidi-labs/HeidiLabsSettings').then((mod) => ({
      default: mod.HeidiLabsSettings,
    })),
  '/settings/team': () =>
    import('@/app-shell/settings/team/TeamSettings').then((mod) => ({
      default: mod.TeamSettings,
    })),
  '/settings/organization': () =>
    import('@/app-shell/settings/organization/OrganizationSettings').then((mod) => ({
      default: mod.OrganizationSettings,
    })),
  '/settings/members': () =>
    import('@/app-shell/settings/members/MembersSettings').then((mod) => ({
      default: mod.MembersSettings,
    })),
  '/settings/templates': () =>
    import('@/app-shell/settings/templates/TemplatesSettings').then((mod) => ({
      default: mod.TemplatesSettings,
    })),
  '/settings/team/evidence': () =>
    import('./settings-screen-entries/team-evidence').then((mod) => ({
      default: mod.TeamEvidenceSettingsScreen,
    })),
  '/settings/usage': () =>
    import('@/app-shell/settings/usage/UsageSettings').then((mod) => ({
      default: mod.UsageSettings,
    })),
  '/settings/api-status': () =>
    import('@/app-shell/settings/api-status/ApiStatusSettings').then((mod) => ({
      default: mod.ApiStatusSettings,
    })),
  '/settings/integrations': () =>
    import('@/app-shell/settings/integrations/IntegrationsSettings').then((mod) => ({
      default: mod.IntegrationsSettings,
    })),
  '/settings/integrations/details': () =>
    import('@/app-shell/settings/integrations/IntegrationMarketplaceDetailsSettings').then(
      (mod) => ({ default: mod.IntegrationMarketplaceDetailsSettings })
    ),
  '/settings/integration-agent': () =>
    import('@heidi/integrations/connected-apps').then((mod) => ({
      default: mod.IntegrationAgentSettings,
    })),
  '/settings/coding': () =>
    import('@heidi/coding').then((mod) => ({
      default: mod.CodingSettings,
    })),
  '/settings/inbox': () =>
    import('@heidi/integrations/document-inbox').then((mod) => ({
      default: mod.InboxSettings,
    })),
  '/settings/nudge-settings': () =>
    import('@/fde/chenmed/nudge-settings').then((mod) => ({
      default: mod.NudgeSettings,
    })),
  '/settings/cloud-computer': () =>
    import('@/app-shell/rpa/cua-cloud/CloudComputerPage').then((mod) => ({
      default: mod.CloudComputerPage,
    })),
  '/settings/version': () =>
    import('@/app-shell/settings/version/VersionSettings').then((mod) => ({
      default: mod.VersionSettings,
    })),
  '/settings/computer-use': () =>
    import('@/app-shell/settings/computer-use/ComputerUseSettings').then((mod) => ({
      default: mod.ComputerUseSettings,
    })),
  '/settings/remote': () =>
    import('@/app-shell/settings/remote/RemoteSettings').then((mod) => ({
      default: mod.RemoteSettings,
    })),
};

/**
 * Where a settings screen body comes from, resolved per route:
 *
 * 1. the registry contribution whose `id` is the route's slug, if a package
 *    declares one — a settings contribution's id **is** the `ROUTE_TO_SLUG`
 *    value (`coding` for `/settings/coding`), which is what lets a package
 *    serve an existing screen with no sidebar or IA change;
 * 2. otherwise {@link rawScreenImporters}, i.e. the app's own import.
 *
 * A contribution whose id is not a known slug is skipped rather than added
 * under a made-up route: it would be unreachable by the modal and invisible in
 * the URL, and the registry is not the place a settings *route* gets minted.
 *
 * A contribution may also declare a host in composition's
 * `settingsScreenHosts` — app chrome a package screen reads from a `configure…`
 * seam because the registry gives it no mount point. It is fetched in PARALLEL
 * with the screen's own chunk, not before it: the two are independent, and the
 * screen only reads the host when it renders, which is after both have settled.
 * A contribution with no host entry is the ordinary case and waits for nothing.
 *
 * The rollback path is wired from the same map, which is the reason the map is
 * keyed by contribution id and this function does the pairing. Withdrawing a
 * descriptor drops its route back to {@link rawScreenImporters}, whose fallback
 * loads the per-screen app entry, which re-exports the screen from its package
 * door — so it needs the same host. The id↔route map connects the two.
 *
 * The result is keyed by route, exactly like the map it replaces, so `SCREENS`,
 * `preloadSettingsScreen`, `WIDE_CONTENT_SCREENS` and `FILL_HEIGHT_SCREENS` all
 * keep keying by route and none of them can tell where a screen came from.
 */
export const resolveScreenImporters = (
  contributions: readonly SettingsScreenContribution[],
  legacyImporters: Record<string, ScreenImporter> = rawScreenImporters,
  hosts: SettingsScreenHostMap = settingsScreenHosts
): Record<string, ScreenImporter> => {
  const resolved: Record<string, ScreenImporter> = { ...legacyImporters };
  const contributed = new Set<string>();

  for (const contribution of contributions) {
    const route = SLUG_TO_ROUTE[contribution.id];
    if (!route) continue;
    contributed.add(route);
    const { load } = contribution;
    const configureHost = settingsScreenHostFor(contribution.id, hosts);
    // The thunk's `default` is `unknown` at the door — discovery is mechanical
    // and cannot assume a component shape — so the cast happens here, at the
    // one place that knows the surface renders a component.
    resolved[route] = () =>
      Promise.all([load(), configureHost?.()]).then(([module]) => ({
        default: module.default as ComponentType,
      }));
  }

  // The rollback path: a host whose contribution has been withdrawn still has to
  // reach the screen through its per-screen app entry. This is where the
  // contribution id becomes a route.
  for (const [id, configureHost] of Object.entries(hosts)) {
    const route = SLUG_TO_ROUTE[id];
    if (!route || !configureHost || contributed.has(route)) continue;
    const legacyImporter = resolved[route];
    if (!legacyImporter) continue;
    resolved[route] = () =>
      Promise.all([legacyImporter(), configureHost()]).then(([module]) => module);
  }

  return resolved;
};

/**
 * Public importer map — same shape as `rawScreenImporters` but each
 * entry is transparently wrapped in {@link settingsImportWithRetry} so
 * `React.lazy` and `preloadSettingsScreen` share identical retry
 * behaviour. New routes added to `rawScreenImporters` inherit it for
 * free, and so does a registry-served route: the wrap happens after
 * resolution, so a contributed chunk gets the same backed-off retries.
 */
const screenImporters: Record<string, ScreenImporter> = Object.fromEntries(
  Object.entries(resolveScreenImporters(appRegistry.settingsScreens)).map(
    ([route, importer]) => [route, () => settingsImportWithRetry(importer)]
  )
);

/**
 * `React.lazy()`-wrapped screen components, keyed by route. `SettingsModalScreen`
 * picks the entry for the active route and renders it inside a Suspense
 * boundary; routes not in this map fall through to the unknown-route recovery
 * path.
 *
 * Adding a new section? Two more touchpoints to consider:
 *
 * 1. `ROUTE_TO_SLUG` in `settingsModalSlugs.ts` — without it, the section is
 *    not reachable via the `?settings=<slug>` URL param.
 * 2. {@link WIDE_CONTENT_SCREENS} below — opt in **only** if your section's
 *    primary content (charts, iframes, wide tables) genuinely doesn't fit
 *    the default `max-w-3xl` reading column.
 */
export const SCREENS: Record<string, ComponentType> = Object.fromEntries(
  Object.entries(screenImporters).map(([route, importer]) => [route, lazy(importer)])
);

/**
 * Routes whose primary content (charts, iframes, wide tables) genuinely
 * doesn't fit inside the default `max-w-3xl` reading column. Listed here,
 * they opt into `<SettingsModalContainer fullWidth>`. Keep this small —
 * the form column exists so text- and form-heavy sections stay scannable.
 */
export const WIDE_CONTENT_SCREENS: ReadonlySet<string> = new Set([
  '/settings/usage',
  // The nudge-settings protocol table is a wide, multi-column data table that overflows the
  // default max-w-3xl reading column; give it the full modal width so it doesn't scroll sideways.
  '/settings/nudge-settings',
  // The org templates library renders a 7-column data table (matching the full-page
  // /organization/templates-library layout); keep it full width so it isn't squeezed.
  '/settings/templates',
  '/settings/integrations/details',
  // Members renders the org/team members table (matching the full-page
  // /organization/members and /team layouts); keep it full width so the
  // multi-column table isn't squeezed into the default reading column.
  '/settings/members',
  // EHR clinic/clinician/appointment-type lists need the wider panel so the
  // master list + slide-over editor have room; also pairs with fillHeight.
  '/settings/comms-integrations',
  // The cloud computer screen viewer embeds a 16:9 VNC iframe of the remote
  // desktop; the default reading column squeezes it too small to be usable.
  '/settings/cloud-computer',
]);

/**
 * Routes whose primary content owns an internal scroll region (dense lists)
 * and must not grow the settings page/modal scroll container.
 * Opted into via `<SettingsModalContainer fillHeight>`.
 */
export const FILL_HEIGHT_SCREENS: ReadonlySet<string> = new Set([
  '/settings/comms-integrations',
]);

/**
 * Hint Webpack/Next to start fetching the chunk for `route` if it hasn't
 * already been loaded. Safe to call repeatedly and from anywhere in the
 * tree — calls for unknown routes are silently ignored, and duplicate calls
 * for in-flight or resolved chunks are a no-op.
 *
 * Used in two places to shrink the perceived "click → form-paint" gap:
 *
 * - {@link useSettingsModal.open} and `setRoute` call this synchronously,
 *   so the chunk fetch races with React's commit work rather than queueing
 *   behind it (Profiler measurements showed this collapsing the
 *   click-to-screen-mounted gap from ~1.3s to ~370ms on localhost dev).
 * - Sidebar items call this on `pointerEnter`/`focus`, so the most likely
 *   *next* section is already in the browser cache by the time the user
 *   clicks.
 */
export const preloadSettingsScreen = (route: string): void => {
  const importer = screenImporters[route];
  if (!importer) return;
  // Fire-and-forget. We swallow any rejection so a transient preload
  // failure doesn't surface as an unhandled rejection — the real `lazy()`
  // render will retry and surface the error through Suspense's error
  // boundary if it still fails. The outer try/catch guards against
  // bundler/test environments where `import()` rejects synchronously.
  try {
    void importer().catch(() => {});
  } catch {
    // No-op.
  }
};
